import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, BarChart3, Zap, Shield, TrendingUp, Globe, CheckCircle, Users, Award, Clock } from 'lucide-react';
import URLForm from '../components/URLForm';

const HomePage = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const features = [
    {
      icon: <Search className="w-6 h-6" />,
      title: 'AI-Powered SEO Analysis',
      description: 'Advanced AI algorithms analyze your website for comprehensive SEO insights and optimization opportunities'
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Detailed SEO Reports',
      description: 'Get comprehensive reports with actionable recommendations, scoring, and performance metrics'
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Instant SEO Results',
      description: 'Receive your complete SEO analysis in seconds, not hours or days like traditional tools'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'AI Discoverability',
      description: 'Optimize your site for AI search engines, chatbots, and modern search algorithms'
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'SEO Performance Tracking',
      description: 'Monitor your SEO improvements over time with detailed analytics and progress reports'
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Global SEO Standards',
      description: 'Follow international SEO best practices and guidelines for maximum search visibility'
    }
  ];

  const seoBenefits = [
    'Improve Google search rankings',
    'Increase organic traffic',
    'Optimize for AI search engines',
    'Enhance user experience',
    'Boost conversion rates',
    'Stay ahead of algorithm changes'
  ];

  const aiFeatures = [
    'Machine learning SEO analysis',
    'Predictive ranking insights',
    'Automated optimization suggestions',
    'Real-time algorithm monitoring',
    'Intelligent keyword research',
    'AI-powered content recommendations'
  ];

  const handleAnalysisStart = () => {
    setIsAnalyzing(true);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              AI-Powered SEO Analysis Tool
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-4xl mx-auto">
              The most advanced AI SEO analysis tool for websites. Get instant SEO insights, 
              keyword analysis, and optimization recommendations. Perfect for AI and SEO professionals 
              looking to improve search engine rankings and AI discoverability.
            </p>
            
            {/* URL Form */}
            <div className="max-w-2xl mx-auto">
              <URLForm onAnalysisStart={handleAnalysisStart} />
            </div>

            <div className="mt-8 text-blue-100">
              <p className="text-sm">
                Trusted by SEO professionals worldwide • No registration required • Instant AI-powered results
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose oneclickAIseo for AI SEO Analysis?
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Our AI-powered platform provides comprehensive SEO analysis that goes beyond 
              traditional tools, helping you optimize for both search engines and AI systems. 
              Get the competitive edge with machine learning-driven insights.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI SEO Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                AI-Powered SEO: The Future of Search Optimization
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Artificial Intelligence is revolutionizing how we approach SEO. Our AI SEO analysis tool 
                uses advanced machine learning algorithms to provide insights that traditional SEO tools simply cannot match.
              </p>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Key AI SEO Benefits:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {aiFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">SEO Analysis Results Include:</h3>
              <div className="space-y-4">
                {seoBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How Our AI SEO Analysis Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get your comprehensive AI-powered SEO analysis in just three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Submit Your Website URL
              </h3>
              <p className="text-gray-600">
                Enter your website URL in the form above and click analyze to start your AI SEO analysis
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                AI-Powered SEO Analysis
              </h3>
              <p className="text-gray-600">
                Our advanced AI crawls your site and analyzes all SEO elements in real-time using machine learning
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Get Comprehensive Results
              </h3>
              <p className="text-gray-600">
                Receive detailed AI-generated reports with scores, insights, and actionable optimization recommendations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trusted by SEO Professionals Worldwide
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join thousands of digital marketers and SEO experts who trust our AI-powered analysis tool
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mx-auto mb-4">
                <Users className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">10,000+ Users</h3>
              <p className="text-gray-600">Trust our AI SEO tool for their website optimization needs</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mx-auto mb-4">
                <Award className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">AI-Powered Accuracy</h3>
              <p className="text-gray-600">Machine learning algorithms provide precise SEO insights and recommendations</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mx-auto mb-4">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Instant Results</h3>
              <p className="text-gray-600">Get comprehensive SEO analysis in seconds, not hours or days</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Optimize Your Website with AI SEO?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Start your free AI-powered SEO analysis now and discover how to improve your website's 
            search engine visibility, AI discoverability, and overall performance. Join the future of SEO optimization.
          </p>
          <Link
            to="/analysis"
            className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            <Search className="w-5 h-5 mr-2" />
            Start Free AI SEO Analysis
          </Link>
        </div>
      </section>

      {/* Development Banner */}
      <section className="py-8 bg-gradient-to-r from-amber-400 to-orange-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center space-x-3">
            <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
            <p className="text-lg font-medium">
              🚧 This application is still in development and getting better every day! 🚧
            </p>
            <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
          </div>
          <p className="text-sm text-amber-100 mt-2">
            We're constantly improving features, performance, and user experience. Your feedback helps us grow!
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
